import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { gql } from "@apollo/client";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { ClinicInsurance } from "../collections/clinic-insurance";
import type { RxDBCollections } from "../provider";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

type WholeSyncableClinicInsuranceFragment = {
  readonly id: string;
  readonly facilityId: string;
  readonly dailyRebateAmount?: number | null;
  readonly healthProviderCode?: string | null;
  readonly meta: {
    readonly createdAt: string;
    readonly createdByUserId: string;
    readonly updatedAt: string;
    readonly updatedByUserId: string;
    readonly deleted: boolean;
  };
};

const WholeSyncableClinicInsuranceFragmentDoc = gql`
  fragment WholeSyncableClinicInsurance on SyncableClinicInsurance {
    id
    facilityId
    dailyRebateAmount
    healthProviderCode
    meta {
      createdAt
      createdByUserId
      updatedAt
      updatedByUserId
      deleted
    }
  }
`;

const SyncClinicInsurancePullDocument = gql`
  query SyncClinicInsurancePull($input: PullClinicInsuranceInput!) {
    pullClinicInsurance(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableClinicInsurance
      }
    }
  }
  ${WholeSyncableClinicInsuranceFragmentDoc}
`;

export const useReplicateClinicInsurance = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<ClinicInsurance>;
}) => {
  const [enabled] = useFeatureFlag<boolean>(featureFlags.billing.rxdbBilling);

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncableClinicInsuranceFragment> | undefined
  >(() => {
    if (!enabled) return undefined;
    return {
      query: SyncClinicInsurancePullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
      reSyncInterval: { minutes: 5 },
    };
  }, [enabled]);

  return useGqlReplication<
    ClinicInsurance,
    WholeSyncableClinicInsuranceFragment,
    null
  >({
    db,
    collection,
    pull,
    mapGqlToRx: mapGqlClinicInsuranceToRxClinicInsurance,
    batchSize: 10,
    metadataLocalStorageKeySuffix: "clinic-insurance",
  });
};

export const mapGqlClinicInsuranceToRxClinicInsurance = (
  doc: WholeSyncableClinicInsuranceFragment,
): WithDeleted<ClinicInsurance> => ({
  id: doc.id,
  facilityId: doc.facilityId,
  dailyRebateAmount: doc.dailyRebateAmount ?? null,
  healthProviderCode: doc.healthProviderCode ?? null,
  meta: {
    createdAt: doc.meta.createdAt,
    createdByUserId: doc.meta.createdByUserId,
    updatedAt: doc.meta.updatedAt,
    updatedByUserId: doc.meta.updatedByUserId,
  },
  _deleted: doc.meta.deleted,
});
